import type {
  AccessLevel,
  AccessMap,
  AdminModuleDef,
  AdminRole,
  ConfigurableRole,
  MockupArea,
  MarketplaceListing,
  MediaUsageFilter,
  MediaUsageTag,
  ProductSkuSave,
  OrderPaymentIssue,
  MockupBlend,
  ProductMockupItemInput,
  ProductSpecs,
} from '@ciptaimaji/shared';
const BASE = process.env.NEXT_PUBLIC_API_URL ?? 'https://api.ciptaimaji.id';

export interface FieldIssue {
  field: string;
  message: string;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: FieldIssue[],
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get displayMessage(): string {
    return this.details?.[0]?.message ?? this.message;
  }
}

interface Envelope<T> {
  data: T;
}

interface ErrorEnvelope {
  error: { code: string; message: string; details?: FieldIssue[] };
}

let refreshing: Promise<boolean> | null = null;

/** Satu kali refresh saat access token kedaluwarsa, lalu permintaan diulang. */
async function tryRefresh(): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  refreshing ??= fetch(`${BASE}/api/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
  })
    .then((res) => res.ok)
    .catch(() => false)
    .finally(() => {
      setTimeout(() => {
        refreshing = null;
      }, 0);
    });
  return refreshing;
}

/** Seluruh panel admin bekerja dengan sesi, jadi credentials selalu disertakan. */
async function request<T>(path: string, init?: RequestInit, allowRetry = true): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: 'include',
    cache: 'no-store',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  });

  if (res.status === 401 && allowRetry && !path.startsWith('/api/auth/')) {
    if (await tryRefresh()) return request<T>(path, init, false);
  }

  if (!res.ok) {
    let code = 'HTTP_ERROR';
    let message = `Permintaan gagal (${res.status})`;
    let issues: FieldIssue[] | undefined;
    try {
      const body = (await res.json()) as ErrorEnvelope;
      code = body.error?.code ?? code;
      message = body.error?.message ?? message;
      issues = body.error?.details;
    } catch {
      // biarkan pesan bawaan
    }
    throw new ApiError(res.status, code, message, issues);
  }

  const body = (await res.json()) as Envelope<T>;
  return body.data;
}

export interface UserDto {
  id: string;
  /** null untuk akun panel internal yang masuk dengan username. */
  email: string | null;
  username: string | null;
  name: string;
  phone: string | null;
  role: 'CUSTOMER' | 'STAFF' | 'ADMIN' | 'SUPER_ADMIN';
  emailVerified: boolean;
}

export interface ArticleTagDto {
  slug: string;
  name: string;
}

export interface MediaRef {
  key: string;
  url: string;
  alt: string;
  width: number;
  height: number;
}

export interface ArticleCardDto {
  slug: string;
  title: string;
  excerpt: string | null;
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  publishedAt: string | null;
  readingMinutes: number;
  updatedAt: string;
  cover: MediaRef | null;
  author: string | null;
  tags: ArticleTagDto[];
}

export interface ArticleDetailDto extends ArticleCardDto {
  contentHtml: string;
  contentJson: unknown;
  seo: { title: string | null; description: string | null };
  coverMediaId: string | null;
}

export interface Paginated<T> {
  items: T[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

export interface ArticlePayload {
  slug?: string;
  title?: string;
  excerpt?: string;
  contentJson?: unknown;
  contentHtml?: string;
  status?: ArticleCardDto['status'];
  seoTitle?: string;
  seoDescription?: string;
  tags?: ArticleTagDto[];
  /** String kosong melepas cover; undefined berarti tidak diubah. */
  coverMediaId?: string;
}

export type MediaRole = 'HERO' | 'DETAIL' | 'CONTEXT' | 'SCALE' | 'SWATCH' | 'EDITORIAL';

export interface MediaAssetDto {
  id: string;
  key: string;
  originalName: string;
  url: string;
  role: MediaRole;
  alt: string;
  width: number;
  height: number;
  bytes: number;
  mimeType: string;
  hasAlpha: boolean;
  blurData: string | null;
  folderId: string | null;
  createdAt: string;
  /** Jumlah pemakaian per tempat, dihitung server dari relasi. */
  usage: Record<MediaUsageTag, number>;
}

export type { MarketplaceListing, MediaUsageFilter, MediaUsageTag, ProductSkuSave };

export type BannerState = 'LIVE' | 'SCHEDULED' | 'ENDED' | 'INACTIVE';

export interface BannerImageDto {
  id: string;
  key: string;
  originalName: string;
  url: string;
  width: number;
  height: number;
  blurData: string | null;
}

export interface BannerDto {
  id: string;
  title: string;
  alt: string;
  href: string;
  opensImma: boolean;
  isActive: boolean;
  startsAt: string | null;
  endsAt: string | null;
  sortOrder: number;
  state: BannerState;
  image: BannerImageDto | null;
  mobileImage: BannerImageDto | null;
  createdAt: string;
  updatedAt: string;
}

export interface BannerPayload {
  title: string;
  alt: string;
  mediaId: string;
  mobileMediaId: string | null;
  href: string;
  opensImma: boolean;
  isActive: boolean;
  startsAt: string | null;
  endsAt: string | null;
}

export type ActivityAction = 'CREATE' | 'UPDATE' | 'DELETE' | 'EXPORT' | 'OTHER';

export interface ActivityRow {
  id: string;
  actorId: string | null;
  actorName: string;
  actorEmail: string;
  actorUsername: string | null;
  actorRole: string;
  module: string | null;
  action: ActivityAction;
  label: string;
  method: string;
  path: string;
  entityType: string | null;
  entityId: string | null;
  createdAt: string;
  changedKeys: string[];
}

export interface ActivityDetail extends Omit<ActivityRow, 'changedKeys'> {
  statusCode: number;
  payload: unknown;
  /** { before, after } berisi kunci yang berubah saja; null bila tidak dipotret. */
  changes:
    { before: unknown; after: unknown } | { truncated: true; bytes: number; keys: string[] } | null;
  ip: string | null;
  userAgent: string | null;
}

export interface ActivityList {
  items: ActivityRow[];
  actors: { id: string; name: string; count: number }[];
  meta: Paginated<never>['meta'];
}

export interface ActivityFilter {
  actorId?: string;
  module?: string;
  action?: ActivityAction;
  entityType?: string;
  entityId?: string;
  q?: string;
  from?: string;
  to?: string;
  page?: number;
}

export interface ProductSkuRow {
  /** Nilai per kelompok variasi, urut sesuai `variations`. */
  values: string[];
  comboKey: string;
  /** null = mengikuti harga produk. */
  price: number | null;
  /** Harga coret sebelum diskon; null = tidak ada. */
  compareAtPrice: number | null;
  stock: number;
  sku: string;
  isActive: boolean;
}

export interface ProductSkuDto {
  product: {
    slug: string;
    name: string;
    description: string;
    status: PublishStatus;
    categorySlug: string;
    tracksStock: boolean;
    weightGram: number;
    lengthCm: number | null;
    widthCm: number | null;
    heightCm: number | null;
    basePrice: number;
    minQty: number;
    qtyStep: number;
    /** Stok produk tanpa variasi. */
    stock: number;
    /** Sama dengan Spesifikasi di tab Detail. */
    specs: { rows: { label: string; value: string }[]; highlights: string[] };
    /** Sama dengan Catatan di tab Detail. */
    notes: string;
    /** Video yang diunggah ke CDN; null berarti belum ada. */
    video: { url: string; mime: string; bytes: number } | null;
  };
  options: { key: string; label: string; values: { value: string; label: string }[] }[];
  variations: { name: string; values: string[] }[];
  rows: ProductSkuRow[];
  listing: MarketplaceListing;
  updatedAt: string | null;
}

export type ReportGroup = 'day' | 'week' | 'month';

export interface ReportSummary {
  range: { from: string; to: string; group: ReportGroup };
  totals: {
    /** Uang masuk: pembayaran lunas menurut tanggal lunas (DP & pelunasan terpisah). */
    moneyIn: number;
    payments: number;
    /** Bagian uang masuk yang pesanannya kini dibatalkan. */
    moneyInCancelled: number;
    ordersPaid: number;
    orderValue: number;
    productSales: number;
    shipping: number;
    averageOrder: number;
  };
  series: { bucket: string; amount: number; payments: number }[];
  byChannel: { channel: PaymentChannel; kind: PaymentKind; payments: number; amount: number }[];
  byProduct: {
    slug: string;
    name: string;
    category: { slug: string; name: string } | null;
    qty: number;
    lines: number;
    amount: number;
  }[];
  productCount: number;
  byCategory: { slug: string | null; name: string; qty: number; amount: number }[];
}

export interface MediaFolderDto {
  id: string;
  name: string;
  parentId: string | null;
  createdAt: string;
  assetCount: number;
  folderCount: number;
}

export interface UploadTicketDto {
  key: string;
  uploadUrl: string;
  expiresInSec: number;
  maxBytes: number;
}

export type PublishStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
export type ProductType = 'CONFIGURABLE' | 'GENERATOR' | 'QUOTE';
export type PriceUnit = 'PIECE' | 'SQUARE_METER' | 'LINEAR_METER';
export type ShippingMode = 'COURIER' | 'PICKUP_ONLY' | 'QUOTE';

export interface AdminProductRow {
  slug: string;
  name: string;
  tagline: string | null;
  type: ProductType;
  status: PublishStatus;
  basePrice: number;
  priceUnit: PriceUnit;
  sortOrder: number;
  updatedAt: string;
  category: { slug: string; name: string };
  cover: { key: string; url: string; alt: string } | null;
  optionCount: number;
  mediaCount: number;
}

export interface AdminProductDetail extends AdminProductRow {
  description: string | null;
  categorySlug: string;
  minQty: number;
  qtyStep: number;
  leadTimeDays: number;
  shippingMode: ShippingMode;
  weightGram: number;
  weightPerUnit: boolean;
  lengthCm: number | null;
  widthCm: number | null;
  heightCm: number | null;
  unitsPerPackage: number | null;
  /** Spesifikasi cetak editor bebas Imaji Studio (KEPUTUSAN 4.7), mm. */
  printWidthMm: number | null;
  printHeightMm: number | null;
  printBleedMm: number | null;
  printSafeMm: number | null;
  freeEditor: boolean;
  /** Alasan editor bebas belum bisa dinyalakan; null = boleh. */
  freeEditorBlocker: string | null;
  printOptions: {
    key: string;
    label: string;
    values: { value: string; label: string; widthMm: number | null; heightMm: number | null }[];
  }[];
  seo: { title: string | null; description: string | null };
  /** Bentuk baku dari shared; data lama sudah dirapikan server. */
  specs: ProductSpecs;
  notes: string | null;
  createdAt: string;
}

export type OptionInputType = 'PILL' | 'SELECT' | 'RANGE' | 'TEXT';

export interface AdminPricingValue {
  value: string;
  label: string;
  helpText: string | null;
  isDefault: boolean;
  priceDelta: number;
  priceFactor: number;
  weightDeltaGram: number;
  widthMm: number | null;
  heightMm: number | null;
  sides: number | null;
}

export interface AdminPricingOption {
  key: string;
  label: string;
  helpText: string | null;
  input: OptionInputType;
  required: boolean;
  values: AdminPricingValue[];
}

export interface AdminPricingOverride {
  selections: Record<string, string>;
  unitPrice: number;
  compareAtPrice: number | null;
}

export interface AdminPricingTier {
  minQty: number;
  factor: number;
  label: string | null;
}

export interface AdminProductPricing {
  slug: string;
  name: string;
  type: ProductType;
  priceUnit: PriceUnit;
  basePrice: number;
  compareAtPrice: number | null;
  minQty: number;
  qtyStep: number;
  /** Terisi = dikelola dari admin; sku:import melewati produk ini. */
  pricingEditedAt: string | null;
  fromSkuPack: boolean;
  options: AdminPricingOption[];
  overrides: AdminPricingOverride[];
  tiers: AdminPricingTier[];
}

export interface ProductPayload {
  name?: string;
  tagline?: string;
  description?: string;
  type?: ProductType;
  status?: PublishStatus;
  categorySlug?: string;
  priceUnit?: PriceUnit;
  basePrice?: number;
  minQty?: number;
  qtyStep?: number;
  leadTimeDays?: number;
  shippingMode?: ShippingMode;
  weightGram?: number;
  weightPerUnit?: boolean;
  lengthCm?: number | null;
  widthCm?: number | null;
  heightCm?: number | null;
  unitsPerPackage?: number | null;
  printWidthMm?: number | null;
  printHeightMm?: number | null;
  printBleedMm?: number | null;
  printSafeMm?: number | null;
  freeEditor?: boolean;
  specs?: ProductSpecs;
  notes?: string;
  seoTitle?: string;
  seoDescription?: string;
  sortOrder?: number;
}

export interface CategoryDto {
  slug: string;
  name: string;
  tagline: string | null;
  parentSlug: string | null;
  productCount: number;
}

export interface AdminCategoryDto {
  slug: string;
  name: string;
  tagline: string | null;
  description: string | null;
  status: PublishStatus;
  sortOrder: number;
  parentSlug: string | null;
  heroMediaId: string | null;
  hero: { key: string; url: string; alt: string; width: number; height: number } | null;
  seo: { title: string | null; description: string | null };
  productCount: number;
  childCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CategoryPayload {
  name?: string;
  tagline?: string;
  description?: string;
  status?: PublishStatus;
  parentSlug?: string | null;
  heroMediaId?: string | null;
  seoTitle?: string;
  seoDescription?: string;
  sortOrder?: number;
}

export interface AdminProductMockup {
  id: string;
  name: string;
  area: MockupArea;
  blend: MockupBlend;
  page: number;
  /** Varian yang cocok untuk mockup ini; kosong = cocok untuk semua varian. */
  selections: Record<string, string>;
  isActive: boolean;
  sortOrder: number;
  media: {
    id: string;
    key: string;
    url: string;
    originalName: string;
    alt: string;
    width: number;
    height: number;
  };
}

export interface ProductMediaRow {
  id: string;
  sortOrder: number;
  isPrimary: boolean;
  media: {
    id: string;
    key: string;
    url: string;
    originalName: string;
    alt: string;
    width: number;
    height: number;
    bytes: number;
    mimeType: string;
  };
}

export type OrderStatus =
  | 'DRAFT'
  | 'PENDING_PAYMENT'
  | 'PAID'
  | 'IN_REVIEW'
  | 'IN_PRODUCTION'
  | 'SHIPPED'
  | 'COMPLETED'
  | 'CANCELLED';

export type PaymentChannel = 'QRIS' | 'VA_BCA';
export type PaymentStatus = 'PENDING' | 'PAID' | 'EXPIRED' | 'CANCELED' | 'FAILED';
export type PaymentKind = 'FULL' | 'DOWN_PAYMENT' | 'SETTLEMENT';

export interface OrderCustomer {
  id: string;
  name: string;
  email: string;
  phone: string | null;
}

export type { OrderPaymentIssue };

export type ImajiProStatus = 'PENDING' | 'SENT' | 'FAILED';

export interface AdminOrderImajiPro {
  status: ImajiProStatus;
  attempts: number;
  lastError: string | null;
  nextAttemptAt: string | null;
  sentAt: string | null;
  updatedAt: string;
  /** Penyapu sudah menyerah; hanya bisa jalan lagi lewat tombol kirim ulang. */
  stuck: boolean;
  externalOrderId: string | null;
}

/** Laporan produksi dari webhook ImajiPRO (arah masuk), bukan antrean kirim. */
export interface AdminOrderProduction {
  /** String mentah dari ImajiPRO; dirapikan di UI lewat productionStatusLabel. */
  status: string;
  syncedAt: string | null;
  paymentStatus: string | null;
  orderCode: string | null;
  items: {
    /** id item pesanan CI; null bila ImajiPRO tidak mengirimkannya. */
    itemId: string | null;
    productName: string | null;
    qty: number;
    stage: string | null;
    finishedAt: string | null;
  }[];
}

export interface AdminOrderRow {
  id: string;
  number: string;
  status: OrderStatus;
  recipient: string;
  customer: OrderCustomer;
  shippingCourier: string;
  shippingService: string;
  trackingNumber: string | null;
  total: number;
  /** Sisa tagihan setelah DP; 0 bila lunas atau belum dibayar sama sekali. */
  balanceDue: number;
  /** Masalah pembayaran yang perlu dicek admin; kosong = aman. */
  paymentIssues: OrderPaymentIssue[];
  /** Antrean kirim ke ImajiPRO; null bila pesanan belum lunas. */
  imajiPro: { status: ImajiProStatus; attempts: number; stuck: boolean } | null;
  /** Status produksi terakhir dari ImajiPRO; null bila belum ada webhook. */
  production: { status: string; syncedAt: string | null } | null;
  itemCount: number;
  paidAt: string | null;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** Desain per item, urut sama dengan item pesanan. source null = belum ada desain. */
  designs: {
    itemId: string;
    productName: string;
    source: 'EDITOR' | 'UPLOAD' | 'GENERATOR' | null;
    fileName: string | null;
    previewUrl: string | null;
    /** Disetujui tim CI; null = belum. */
    approvedAt: string | null;
  }[];
}

export interface AdminOrderItem {
  id: string;
  productSlug: string;
  productName: string;
  qty: number;
  optionLabels: { key: string; label: string; value: string; valueLabel: string }[];
  unitPrice: number;
  totalPrice: number;
  designSource: 'EDITOR' | 'UPLOAD' | 'GENERATOR' | null;
  designFileKey: string | null;
  designFileName: string | null;
  /** URL bertanda tangan berumur pendek; null untuk PDF/AI/CDR. */
  designPreviewUrl: string | null;
  designApprovedAt: string | null;
  /** Jumlah sisi desain (KEPUTUSAN 4.6); halaman 2 dan seterusnya di designPages. */
  sides: number;
  designPages: {
    page: number;
    /** Dicetak dengan desain halaman 1. */
    sameAsFirst: boolean;
    designSource: 'EDITOR' | 'UPLOAD' | 'GENERATOR' | null;
    designFileKey: string | null;
    designFileName: string | null;
    designPreviewUrl: string | null;
  }[];
  /** Staf yang menyetujui desain; null bila belum disetujui. */
  designApprovedBy?: { id: string; name: string } | null;
  /** Riwayat desain item, urut dari yang paling awal. */
  designEvents: {
    id: string;
    action: 'ATTACHED' | 'REPLACED' | 'APPROVED' | 'APPROVAL_REVOKED';
    actor: { id: string; name: string; isCustomer: boolean } | null;
    file: string | null;
    previousFile: string | null;
    /** Halaman desain yang berubah; 1 untuk desain utama. */
    page: number;
    note: string | null;
    createdAt: string;
  }[];
}

export interface AdminOrderHistory {
  id: string;
  from: OrderStatus | null;
  to: OrderStatus;
  note: string | null;
  /** null berarti sistem: checkout atau callback pembayaran. */
  actor: { id: string; name: string } | null;
  createdAt: string;
}

export interface AdminOrderPayment {
  id: string;
  channel: PaymentChannel;
  status: PaymentStatus;
  kind: PaymentKind;
  amount: number;
  providerRef: string | null;
  vaNumber: string | null;
  paymentUrl: string | null;
  expiresAt: string | null;
  paidAt: string | null;
  failReason: string | null;
  createdAt: string;
}

export interface AdminOrderDetail {
  id: string;
  number: string;
  status: OrderStatus;
  /** Langkah yang server izinkan dari status sekarang; UI tidak menebak sendiri. */
  allowedTransitions: OrderStatus[];
  customer: OrderCustomer;
  recipient: string;
  phone: string;
  addressLine: string;
  addressNote: string | null;
  postalCode: string;
  destinationLabel: string;
  shippingCourier: string;
  shippingCourierName: string;
  shippingService: string;
  shippingEtd: string | null;
  shippingWeightGram: number;
  shippingCost: number;
  trackingNumber: string | null;
  subtotal: number;
  discount: number;
  total: number;
  amountPaid: number;
  balanceDue: number;
  paymentIssues: OrderPaymentIssue[];
  imajiPro: AdminOrderImajiPro | null;
  production: AdminOrderProduction | null;
  /** null berarti dibayar penuh, bukan DP. */
  downPaymentAmount: number | null;
  settledAt: string | null;
  paymentMethod: string | null;
  paymentRef: string | null;
  paidAt: string | null;
  expiresAt: string | null;
  customerNote: string | null;
  internalNote: string | null;
  createdAt: string;
  updatedAt: string;
  items: AdminOrderItem[];
  history: AdminOrderHistory[];
  payments: AdminOrderPayment[];
}

export type UserStatus = 'ACTIVE' | 'SUSPENDED';

export interface AdminCustomerRow {
  id: string;
  email: string;
  name: string;
  phone: string | null;
  role: UserDto['role'];
  status: UserStatus;
  emailVerified: boolean;
  orderCount: number;
  addressCount: number;
  totalSpent: number;
  lastOrderAt: string | null;
  createdAt: string;
}

export interface AdminCustomerDetail extends AdminCustomerRow {
  updatedAt: string;
  addresses: {
    id: string;
    label: string | null;
    recipient: string;
    phone: string;
    line: string;
    note: string | null;
    destinationLabel: string | null;
    cityName: string;
    provinceName: string;
    postalCode: string;
    isDefault: boolean;
  }[];
  orders: {
    number: string;
    status: OrderStatus;
    total: number;
    itemCount: number;
    paidAt: string | null;
    createdAt: string;
  }[];
}

export interface AdminOrderList {
  items: AdminOrderRow[];
  counts: Partial<Record<OrderStatus, number>>;
  meta: Paginated<never>['meta'];
}

export interface RevenueWindow {
  amount: number;
  orders: number;
}

export interface AdminDashboard {
  generatedAt: string;
  /** null untuk STAFF: omzet hanya ditampilkan ke ADMIN. */
  revenue: { today: RevenueWindow; last7Days: RevenueWindow; thisMonth: RevenueWindow } | null;
  orders: {
    pendingPayment: number;
    paid: number;
    inReview: number;
    inProduction: number;
    shipped: number;
    /** Dibayar/menunggu persetujuan dan masih punya desain yang belum disetujui. */
    designPendingApproval: number;
    /** Kelebihan bayar, bayar setelah batal, atau tagihan tak terkonfirmasi. */
    paymentIssues: number;
    /** Antrean ImajiPRO yang sudah menyerah dan perlu dikirim ulang. */
    imajiProStuck: number;
  };
  needsAction: {
    number: string;
    status: OrderStatus;
    customerName: string;
    total: number;
    paidAt: string | null;
    itemCount: number;
    itemsWithoutDesign: number;
    itemsWithDesign: number;
    itemsDesignApproved: number;
  }[];
  expiringSoon: {
    number: string;
    customerName: string;
    phone: string | null;
    total: number;
    expiresAt: string | null;
  }[];
  catalog: { published: number; draft: number; withoutPhoto: number };
  content: { published: number; draft: number };
}

export type PageKindDto = 'PAGE' | 'SERVICE';

export interface AdminPageCard {
  kind: PageKindDto;
  slug: string;
  title: string;
  excerpt: string | null;
  status: PublishStatus;
  publishedAt: string | null;
  sortOrder: number;
  priceNote: string | null;
  updatedAt: string;
  cover: { key: string; url: string; alt: string; width: number; height: number } | null;
}

export interface AdminPageDetail extends AdminPageCard {
  contentJson: unknown;
  contentHtml: string;
  seo: { title: string | null; description: string | null };
  coverMediaId: string | null;
  ctaLabel: string | null;
  ctaMessage: string | null;
}

export interface PagePayload {
  title?: string;
  excerpt?: string | null;
  contentJson?: unknown;
  contentHtml?: string;
  status?: PublishStatus;
  sortOrder?: number;
  seoTitle?: string | null;
  seoDescription?: string | null;
  coverMediaId?: string | null;
  priceNote?: string | null;
  ctaLabel?: string | null;
  ctaMessage?: string | null;
}

const PAGE_BASE: Record<PageKindDto, string> = {
  PAGE: '/api/admin/pages',
  SERVICE: '/api/admin/services',
};

export type PanelRole = 'SUPER_ADMIN' | 'ADMIN' | 'STAFF';

export interface AdminUserRow {
  id: string;
  username: string | null;
  email: string | null;
  name: string;
  /** Peran teknis: SUPER_ADMIN, atau ADMIN untuk akun panel bersama roleRef. */
  role: 'SUPER_ADMIN' | 'ADMIN' | 'STAFF';
  /** Nilai pilihan role di admin: id role panel atau SUPER_ADMIN. */
  roleRef: string | null;
  roleName: string;
  status: UserStatus;
  createdAt: string;
  lastLoginAt: string | null;
  failedLoginCount: number;
  lastFailedLoginAt: string | null;
  /** Terisi = login ditolak sampai super admin membuka kunci. */
  lockedAt: string | null;
  activeSessions: number;
}

export interface AdminUserDetail extends AdminUserRow {
  sessions: {
    id: string;
    createdAt: string;
    expiresAt: string;
    userAgent: string | null;
    ip: string | null;
  }[];
}

export interface PanelRoleDef {
  id: string;
  name: string;
  /** Terisi untuk dua role bawaan hasil migrasi (ADMIN/STAFF). */
  legacy: 'ADMIN' | 'STAFF' | null;
  sortOrder: number;
  userCount: number;
  permissions: AccessMap;
}

export interface AccessMatrix {
  modules: AdminModuleDef[];
  roles: PanelRoleDef[];
  /** Kolom super admin: selalu penuh dan tidak bisa diubah. */
  superAdmin: AccessMap;
  defaults: Record<ConfigurableRole, AccessMap>;
}

export const api = {
  /** Khusus super admin: akun panel, sandi, dan sesi. */
  adminUsers: {
    list: () => request<AdminUserRow[]>('/api/admin/users'),
    detail: (id: string) => request<AdminUserDetail>(`/api/admin/users/${id}`),
    create: (payload: {
      name: string;
      username: string;
      email: string | null;
      /** Id role panel atau SUPER_ADMIN. */
      role: string;
      password: string;
    }) =>
      request<AdminUserDetail>('/api/admin/users', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    promote: (payload: { email: string; username: string; role: string }) =>
      request<AdminUserDetail>('/api/admin/users/promote', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    update: (
      id: string,
      payload: {
        name?: string;
        username?: string;
        email?: string | null;
        /** Id role panel, SUPER_ADMIN, atau CUSTOMER untuk mencabut akses. */
        role?: string;
        status?: UserStatus;
      },
    ) =>
      request<AdminUserDetail | { id: string; removed: true }>(`/api/admin/users/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),
    setPassword: (id: string, password: string) =>
      request<AdminUserDetail>(`/api/admin/users/${id}/password`, {
        method: 'PUT',
        body: JSON.stringify({ password }),
      }),
    unlock: (id: string) =>
      request<AdminUserDetail>(`/api/admin/users/${id}/unlock`, { method: 'POST' }),
    revokeSession: (id: string, sessionId: string) =>
      request<AdminUserDetail>(`/api/admin/users/${id}/sessions/${sessionId}`, {
        method: 'DELETE',
      }),
    revokeAllSessions: (id: string) =>
      request<AdminUserDetail>(`/api/admin/users/${id}/sessions`, { method: 'DELETE' }),
  },

  /** Halaman (PAGE) dan layanan (SERVICE) memakai endpoint yang sama bentuknya. */
  pages: (kind: PageKindDto) => ({
    list: (status?: PublishStatus) =>
      request<AdminPageCard[]>(`${PAGE_BASE[kind]}${status ? `?status=${status}` : ''}`),
    detail: (slug: string) => request<AdminPageDetail>(`${PAGE_BASE[kind]}/${slug}`),
    create: (payload: PagePayload & { slug: string }) =>
      request<AdminPageDetail>(PAGE_BASE[kind], { method: 'POST', body: JSON.stringify(payload) }),
    update: (slug: string, payload: PagePayload) =>
      request<AdminPageDetail>(`${PAGE_BASE[kind]}/${slug}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),
    remove: (slug: string) =>
      request<{ slug: string }>(`${PAGE_BASE[kind]}/${slug}`, { method: 'DELETE' }),
  }),

  access: {
    mine: () => request<{ role: AdminRole; permissions: AccessMap }>('/api/admin/access/me'),
    matrix: () => request<AccessMatrix>('/api/admin/access/roles'),

    createRole: (name: string) =>
      request<AccessMatrix & { createdId: string }>('/api/admin/access/roles', {
        method: 'POST',
        body: JSON.stringify({ name }),
      }),

    /** Kirim hanya modul yang berubah; sisanya tetap seperti tersimpan. */
    updateRole: (
      id: string,
      payload: { name?: string; permissions?: Record<string, AccessLevel> },
    ) =>
      request<AccessMatrix>(`/api/admin/access/roles/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),

    removeRole: (id: string) =>
      request<AccessMatrix>(`/api/admin/access/roles/${id}`, { method: 'DELETE' }),
  },

  dashboard: {
    summary: () => request<AdminDashboard>('/api/admin/dashboard'),
  },

  auth: {
    /** Panel admin masuk dengan username, bukan email. */
    login: (payload: { username: string; password: string }) =>
      request<UserDto>('/api/auth/panel/login', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    logout: () => request<{ ok: boolean }>('/api/auth/logout', { method: 'POST' }),
    me: () => request<UserDto>('/api/auth/me'),
  },

  categories: {
    /** Publik: hanya yang tayang. Dipakai pilihan kategori di editor produk. */
    list: () => request<CategoryDto[]>('/api/categories'),

    /** Admin: semua status. */
    listAll: (params?: { status?: PublishStatus }) => {
      const suffix = params?.status ? `?status=${params.status}` : '';
      return request<AdminCategoryDto[]>(`/api/admin/categories${suffix}`);
    },

    detail: (slug: string) => request<AdminCategoryDto>(`/api/admin/categories/${slug}`),

    create: (payload: CategoryPayload & { slug: string }) =>
      request<AdminCategoryDto>('/api/admin/categories', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),

    update: (slug: string, payload: CategoryPayload) =>
      request<AdminCategoryDto>(`/api/admin/categories/${slug}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),

    remove: (slug: string) =>
      request<{ slug: string }>(`/api/admin/categories/${slug}`, { method: 'DELETE' }),
  },

  products: {
    list: (params?: { status?: PublishStatus; category?: string; q?: string; limit?: number }) => {
      const q = new URLSearchParams();
      if (params?.status) q.set('status', params.status);
      if (params?.category) q.set('category', params.category);
      if (params?.q) q.set('q', params.q);
      if (params?.limit) q.set('limit', String(params.limit));
      const suffix = q.toString() ? `?${q.toString()}` : '';
      return request<AdminProductRow[]>(`/api/admin/products${suffix}`);
    },

    detail: (slug: string) => request<AdminProductDetail>(`/api/admin/products/${slug}`),

    create: (payload: ProductPayload & { slug: string }) =>
      request<AdminProductDetail>('/api/admin/products', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),

    update: (slug: string, payload: ProductPayload) =>
      request<AdminProductDetail>(`/api/admin/products/${slug}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),

    remove: (slug: string) =>
      request<{ slug: string }>(`/api/admin/products/${slug}`, { method: 'DELETE' }),

    media: (slug: string) => request<ProductMediaRow[]>(`/api/admin/products/${slug}/media`),

    setMedia: (slug: string, items: { mediaId: string; isPrimary: boolean }[]) =>
      request<ProductMediaRow[]>(`/api/admin/products/${slug}/media`, {
        method: 'PUT',
        body: JSON.stringify({ items }),
      }),

    mockups: (slug: string) => request<AdminProductMockup[]>(`/api/admin/products/${slug}/mockups`),

    setMockups: (slug: string, items: ProductMockupItemInput[]) =>
      request<AdminProductMockup[]>(`/api/admin/products/${slug}/mockups`, {
        method: 'PUT',
        body: JSON.stringify({ items }),
      }),

    pricing: (slug: string) => request<AdminProductPricing>(`/api/admin/products/${slug}/pricing`),

    savePricing: (
      slug: string,
      payload: Pick<AdminProductPricing, 'options' | 'overrides' | 'tiers'>,
    ) =>
      request<AdminProductPricing>(`/api/admin/products/${slug}/pricing`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      }),
  },

  orders: {
    list: async (params?: {
      status?: OrderStatus;
      design?: 'missing' | 'pending' | 'approved';
      payment?: 'issue';
      sync?: 'stuck' | 'pending' | 'sent';
      q?: string;
      page?: number;
      limit?: number;
    }): Promise<AdminOrderList> => {
      const q = new URLSearchParams();
      if (params?.status) q.set('status', params.status);
      if (params?.design) q.set('design', params.design);
      if (params?.payment) q.set('payment', params.payment);
      if (params?.sync) q.set('sync', params.sync);
      if (params?.q) q.set('q', params.q);
      if (params?.page) q.set('page', String(params.page));
      if (params?.limit) q.set('limit', String(params.limit));
      const suffix = q.toString() ? `?${q.toString()}` : '';
      // Daftar pesanan membawa dua amplop tambahan (counts, meta) di luar data,
      // jadi tidak lewat request<T> yang hanya mengembalikan data.
      const res = await fetch(`${BASE}/api/admin/orders${suffix}`, {
        credentials: 'include',
        cache: 'no-store',
      });
      if (res.status === 401 && (await tryRefresh())) return api.orders.list(params);
      if (!res.ok) throw new ApiError(res.status, 'HTTP_ERROR', `Permintaan gagal (${res.status})`);
      const body = (await res.json()) as {
        data: AdminOrderRow[];
        counts: AdminOrderList['counts'];
        meta: AdminOrderList['meta'];
      };
      return { items: body.data, counts: body.counts, meta: body.meta };
    },

    detail: (orderNumber: string) =>
      request<AdminOrderDetail>(`/api/admin/orders/${encodeURIComponent(orderNumber)}`),

    setStatus: (
      orderNumber: string,
      payload: { to: OrderStatus; note?: string; trackingNumber?: string },
    ) =>
      request<AdminOrderDetail>(`/api/admin/orders/${encodeURIComponent(orderNumber)}/status`, {
        method: 'POST',
        body: JSON.stringify(payload),
      }),

    /** Tautan unduh berumur 10 menit; minta ulang setiap kali mengunduh. */
    designUrl: (orderNumber: string, itemId: string, page = 1) =>
      request<{ url: string; fileName: string | null }>(
        `/api/admin/orders/${encodeURIComponent(orderNumber)}/items/${encodeURIComponent(itemId)}/design${page > 1 ? `?page=${page}` : ''}`,
      ),

    /** approve=false membatalkan persetujuan. Hanya saat pesanan PAID/IN_REVIEW. */
    setDesignApproval: (orderNumber: string, itemId: string, approve: boolean, note?: string) =>
      request<AdminOrderDetail>(
        `/api/admin/orders/${encodeURIComponent(orderNumber)}/items/${encodeURIComponent(itemId)}/design/approval`,
        {
          method: approve ? 'POST' : 'DELETE',
          body: JSON.stringify(note?.trim() ? { note: note.trim() } : {}),
        },
      ),

    /** Menaruh ulang pesanan di antrean ImajiPRO setelah penyapu menyerah. */
    retryImajiPro: (orderNumber: string) =>
      request<AdminOrderDetail>(
        `/api/admin/orders/${encodeURIComponent(orderNumber)}/imajipro/retry`,
        { method: 'POST' },
      ),

    /** Tanya status tagihan ke Komerce; bila sudah dibayar, pesanan ikut diperbarui. */
    checkPayment: (orderNumber: string, paymentId: string) =>
      request<{ providerStatus: string; paymentStatus: PaymentStatus }>(
        `/api/admin/orders/${encodeURIComponent(orderNumber)}/payments/${encodeURIComponent(paymentId)}/check`,
        { method: 'POST' },
      ),

    update: (orderNumber: string, payload: { internalNote?: string; trackingNumber?: string }) =>
      request<AdminOrderDetail>(`/api/admin/orders/${encodeURIComponent(orderNumber)}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),
  },

  customers: {
    list: async (params?: {
      q?: string;
      role?: UserDto['role'];
      status?: UserStatus;
      page?: number;
      limit?: number;
    }): Promise<Paginated<AdminCustomerRow>> => {
      const q = new URLSearchParams();
      if (params?.q) q.set('q', params.q);
      if (params?.role) q.set('role', params.role);
      if (params?.status) q.set('status', params.status);
      if (params?.page) q.set('page', String(params.page));
      if (params?.limit) q.set('limit', String(params.limit));
      const suffix = q.toString() ? `?${q.toString()}` : '';
      // Sama seperti pesanan: meta ada di luar data, jadi tidak lewat request<T>.
      const res = await fetch(`${BASE}/api/admin/customers${suffix}`, {
        credentials: 'include',
        cache: 'no-store',
      });
      if (res.status === 401 && (await tryRefresh())) return api.customers.list(params);
      if (!res.ok) throw new ApiError(res.status, 'HTTP_ERROR', `Permintaan gagal (${res.status})`);
      const body = (await res.json()) as {
        data: AdminCustomerRow[];
        meta: Paginated<never>['meta'];
      };
      return { items: body.data, meta: body.meta };
    },

    detail: (id: string) => request<AdminCustomerDetail>(`/api/admin/customers/${id}`),

    update: (id: string, payload: { status?: UserStatus; role?: UserDto['role'] }) =>
      request<AdminCustomerDetail>(`/api/admin/customers/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),
  },

  activity: {
    list: async (filter: ActivityFilter): Promise<ActivityList> => {
      const q = new URLSearchParams();
      for (const [key, value] of Object.entries(filter)) {
        if (value !== undefined && value !== '') q.set(key, String(value));
      }
      const url = `${BASE}/api/admin/activity?${q.toString()}`;
      // Amplop berisi actors dan meta di luar data, jadi tidak lewat request<T>.
      let res = await fetch(url, { credentials: 'include', cache: 'no-store' });
      if (res.status === 401 && (await tryRefresh())) {
        res = await fetch(url, { credentials: 'include', cache: 'no-store' });
      }
      if (!res.ok) throw new ApiError(res.status, 'HTTP_ERROR', `Permintaan gagal (${res.status})`);
      const body = (await res.json()) as {
        data: ActivityRow[];
        actors: ActivityList['actors'];
        meta: ActivityList['meta'];
      };
      return { items: body.data, actors: body.actors, meta: body.meta };
    },
    detail: (id: string) =>
      request<ActivityDetail>(`/api/admin/activity/${encodeURIComponent(id)}`),
  },

  productSku: {
    get: (slug: string) => request<ProductSkuDto>(`/api/admin/products/${slug}/sku`),
    /** Produk baru dibuat dari form yang sama; server membuat barisnya lalu menyimpan SKU. */
    create: (payload: ProductSkuSave & { slug: string; type: ProductType }) =>
      request<ProductSkuDto>('/api/admin/products/sku', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    save: (slug: string, payload: ProductSkuSave) =>
      request<ProductSkuDto>(`/api/admin/products/${slug}/sku`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      }),
    /** Tiket unggah video langsung ke R2; berkas tidak melewati server kita. */
    videoTicket: (slug: string, payload: { mimeType: string; sizeBytes: number }) =>
      request<{ key: string; uploadUrl: string; expiresInSec: number; maxBytes: number }>(
        `/api/admin/products/${slug}/video/uploads`,
        { method: 'POST', body: JSON.stringify(payload) },
      ),

    commitVideo: (slug: string, key: string) =>
      request<{ url: string; mime: string; bytes: number }>(`/api/admin/products/${slug}/video`, {
        method: 'PUT',
        body: JSON.stringify({ key }),
      }),

    removeVideo: (slug: string) =>
      request<{ removed: boolean }>(`/api/admin/products/${slug}/video`, { method: 'DELETE' }),

    /** CSV diunduh lewat fetch (cookie ikut), lalu disimpan dari blob. */
    downloadCsv: async (slug: string) => {
      const url = `${BASE}/api/admin/products/${slug}/sku/export.csv`;
      let res = await fetch(url, { credentials: 'include', cache: 'no-store' });
      if (res.status === 401 && (await tryRefresh())) {
        res = await fetch(url, { credentials: 'include', cache: 'no-store' });
      }
      if (!res.ok) throw new ApiError(res.status, 'HTTP_ERROR', `Permintaan gagal (${res.status})`);
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `sku-${slug}.csv`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    },
  },

  reports: {
    summary: (params: { from: string; to: string; group: ReportGroup }) =>
      request<ReportSummary>(
        `/api/admin/reports/summary?${new URLSearchParams(params).toString()}`,
      ),

    /** CSV diunduh lewat fetch (cookie ikut), lalu disimpan dari blob. */
    downloadOrdersCsv: async (params: { from: string; to: string }) => {
      const url = `${BASE}/api/admin/reports/orders.csv?${new URLSearchParams(params).toString()}`;
      let res = await fetch(url, { credentials: 'include', cache: 'no-store' });
      if (res.status === 401 && (await tryRefresh())) {
        res = await fetch(url, { credentials: 'include', cache: 'no-store' });
      }
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as ErrorEnvelope | null;
        throw new ApiError(
          res.status,
          body?.error?.code ?? 'HTTP_ERROR',
          body?.error?.message ?? `Permintaan gagal (${res.status})`,
          body?.error?.details,
        );
      }
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `pesanan-${params.from}-sd-${params.to}.csv`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    },
  },

  banners: {
    list: () => request<BannerDto[]>('/api/admin/banners'),
    create: (payload: BannerPayload) =>
      request<BannerDto>('/api/admin/banners', { method: 'POST', body: JSON.stringify(payload) }),
    update: (id: string, payload: Partial<BannerPayload>) =>
      request<BannerDto>(`/api/admin/banners/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),
    remove: (id: string) =>
      request<{ id: string }>(`/api/admin/banners/${id}`, { method: 'DELETE' }),
    reorder: (ids: string[]) =>
      request<BannerDto[]>('/api/admin/banners/order', {
        method: 'PUT',
        body: JSON.stringify({ ids }),
      }),
  },

  media: {
    /** folder: id folder, `root` untuk akar, kosong untuk semua folder. */
    list: (params?: {
      role?: MediaRole;
      q?: string;
      folder?: string;
      usage?: MediaUsageFilter;
      page?: number;
      limit?: number;
    }) => {
      const q = new URLSearchParams();
      if (params?.role) q.set('role', params.role);
      if (params?.folder) q.set('folder', params.folder);
      if (params?.usage) q.set('usage', params.usage);
      if (params?.q) q.set('q', params.q);
      if (params?.page) q.set('page', String(params.page));
      if (params?.limit) q.set('limit', String(params.limit));
      const suffix = q.toString() ? `?${q.toString()}` : '';
      return request<MediaAssetDto[]>(`/api/admin/media${suffix}`);
    },

    ticket: (payload: { mimeType: string; sizeBytes: number }) =>
      request<UploadTicketDto>('/api/admin/media/uploads', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),

    commit: (payload: {
      key: string;
      role?: MediaRole;
      alt?: string;
      originalName?: string;
      folderId?: string | null;
    }) =>
      request<MediaAssetDto>('/api/admin/media/commit', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),

    update: (id: string, payload: { role?: MediaRole; alt?: string; folderId?: string | null }) =>
      request<MediaAssetDto>(`/api/admin/media/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),

    remove: (id: string) =>
      request<{ id: string; key: string }>(`/api/admin/media/${id}`, { method: 'DELETE' }),

    folders: () => request<MediaFolderDto[]>('/api/admin/media/folders'),

    createFolder: (payload: { name: string; parentId: string | null }) =>
      request<MediaFolderDto>('/api/admin/media/folders', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),

    updateFolder: (id: string, payload: { name?: string; parentId?: string | null }) =>
      request<MediaFolderDto>(`/api/admin/media/folders/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),

    removeFolder: (id: string) =>
      request<{ id: string }>(`/api/admin/media/folders/${id}`, { method: 'DELETE' }),
  },

  articles: {
    list: (params?: { status?: string; page?: number }) => {
      const q = new URLSearchParams();
      if (params?.status) q.set('status', params.status);
      if (params?.page) q.set('page', String(params.page));
      const suffix = q.toString() ? `?${q.toString()}` : '';
      return request<Paginated<ArticleCardDto>>(`/api/admin/articles${suffix}`);
    },
    detail: (slug: string) => request<ArticleDetailDto>(`/api/admin/articles/${slug}`),
    create: (payload: ArticlePayload) =>
      request<ArticleDetailDto>('/api/admin/articles', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    update: (slug: string, payload: ArticlePayload) =>
      request<ArticleDetailDto>(`/api/admin/articles/${slug}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),
    remove: (slug: string) =>
      request<{ slug: string }>(`/api/admin/articles/${slug}`, { method: 'DELETE' }),
  },
};
